/**
 * Unit conversion utilities for height and weight.
 */

import type { TextFieldValidationType, TextField } from "@/commonTypes/question";

const CM_PER_INCH = 2.54;
const INCHES_PER_FOOT = 12;
const KG_PER_LB = 0.453592;

export const IMPERIAL_TYPES = new Set<TextFieldValidationType>(["height_ft", "height_in", "weight_lbs"]);
export const METRIC_TYPES = new Set<TextFieldValidationType>(["height_cm", "weight_kg"]);

export function roundTo(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

/** Converts feet + inches to centimeters. */
export function imperialHeightToCm(ft: number, inches: number): number {
  return (ft * INCHES_PER_FOOT + inches) * CM_PER_INCH;
}

/** Converts centimeters to { ft, inches }. */
export function cmToImperialHeight(cm: number): { ft: number; inches: number } {
  const totalInches = cm / CM_PER_INCH;
  const ft = Math.floor(totalInches / INCHES_PER_FOOT);
  const inches = Math.round(totalInches % INCHES_PER_FOOT);

  if (inches === INCHES_PER_FOOT) {
    return { ft: ft + 1, inches: 0 };
  }
  return { ft, inches };
}

export function lbsToKg(lbs: number): number {
  return lbs * KG_PER_LB;
}

export function kgToLbs(kg: number): number {
  return kg / KG_PER_LB;
}

/**
 * Auto-converts unit fields when the user edits one system,
 * updating the other system's fields in place.
 */
export function autoConvertUnits(
  changedField: TextField,
  answers: Record<string, string>,
  allFields: TextField[],
): void {
  const val = parseFloat(answers[changedField.id]);
  const field = (type: TextFieldValidationType) => allFields.find((f) => f.validationType === type);

  switch (changedField.validationType) {
    case "height_ft":
    case "height_in": {
      const cm = field("height_cm");
      if (!cm) return;
      const ft = parseFloat(answers[field("height_ft")?.id ?? ""]);
      const inches = parseFloat(answers[field("height_in")?.id ?? ""]);

      // Keep inches bounded to 11 without carrying overflow into feet.
      let boundedInches = isNaN(inches) ? 0 : inches;
      if (boundedInches > 11) {
        boundedInches = 11;
        // Update the users answer to the bounded value of 11 inches
        if (changedField.validationType === "height_in") {
          answers[changedField.id] = String(boundedInches);
        }
      }

      answers[cm.id] = isNaN(ft) && isNaN(inches)
        ? ""
        : String(roundTo(imperialHeightToCm(ft || 0, boundedInches), 1));
      break;
    }
    case "height_cm": {
      const ftF = field("height_ft");
      const inF = field("height_in");
      if (!ftF || !inF) return;
      if (isNaN(val)) { answers[ftF.id] = ""; answers[inF.id] = ""; }
      else { const { ft, inches } = cmToImperialHeight(val); answers[ftF.id] = String(ft); answers[inF.id] = String(inches); }
      break;
    }
    case "weight_lbs": {
      const kg = field("weight_kg");
      if (kg) answers[kg.id] = isNaN(val) ? "" : String(roundTo(lbsToKg(val), 1));
      break;
    }
    case "weight_kg": {
      const lbs = field("weight_lbs");
      if (lbs) answers[lbs.id] = isNaN(val) ? "" : String(Math.round(kgToLbs(val)));
      break;
    }
  }
}

